
import React, { useState, useEffect } from "react";
import AxiosInstance from "../../../AxiosInstance";
import { formatXaf, formatEurFromXaf } from "../../../utils/currency";
import { Trash2 } from "lucide-react";
import './styles/stepMangement.css'
const ServiceList = ({
    cities,
    categories,
    selectedCity,
    setSelectedCity,
    showModal,
    showConfirmDialog,
    setCurrentServiceId,
    setEditingServiceId,
    loadServiceEtapes,
    safeSetState,
    isMountedRef,
    setTab,
    startEditService
}) => {
    const [services, setServices] = useState([]);
    const [stats, setStats] = useState(null);
    const [arrondissements, setArrondissements] = useState([]);
    const [selectedArrondissement, setSelectedArrondissement] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [packId, setPackId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const totalPages = Math.ceil(services.length / itemsPerPage);

    const paginatedServices = services.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    useEffect(() => {
        if (selectedCity) {
            const fetchData = async () => {
                try {
                    const [arrRes, statsRes] = await Promise.all([
                        AxiosInstance.get(`/api/cities/${selectedCity}/arrondissements`),
                        AxiosInstance.get(`/api/services/stats/city/${selectedCity}`)
                    ]);

                    if (isMountedRef.current) {
                        safeSetState(setArrondissements, arrRes.data.arrondissements || arrRes.data || []);
                        safeSetState(setStats, statsRes.data);
                    }
                } catch (error) {
                    console.error("Erreur lors du chargement des données:", error);
                    if (isMountedRef.current) {
                        safeSetState(setArrondissements, []);
                        safeSetState(setStats, null);
                    }
                }
            };
            fetchData();
        } else {
            safeSetState(setArrondissements, []);
            safeSetState(setStats, null);
        }
    }, [selectedCity, isMountedRef, safeSetState]);

    useEffect(() => {
        const fetchPack = async () => {
            try {
                const res = await AxiosInstance.get(`/api/packs`);
                const packs = res.data?.data;
                if (Array.isArray(packs) && packs.length > 0) {
                    setPackId(packs[0]._id);
                }
            } catch (err) {
                console.error("Erreur lors de la récupération des packs :", err);
            }
        };

        fetchPack();
    }, []);

    useEffect(() => {
        if (selectedCity || selectedArrondissement || searchQuery.trim()) {
            loadServices();
        } else {
            safeSetState(setServices, []);
        }
    }, [searchQuery, selectedCity, selectedArrondissement]);

    // Ajouté selectedCity ici pour reset la pagination aussi quand la ville change
    useEffect(() => {
        setCurrentPage(1);
    }, [selectedCity, selectedArrondissement, searchQuery]);

    const loadServices = async () => {
        if (!isMountedRef.current) return;

        try {
            let response;

            if (searchQuery.trim()) {
                response = await AxiosInstance.get(`/api/services/service/search?title=${encodeURIComponent(searchQuery.trim())}`);
            } else if (selectedArrondissement) {
                setLoading(true);
                response = await AxiosInstance.get(`/api/services/arrondissement/${selectedArrondissement}`);
            } else if (selectedCity) {
                setLoading(true);
                response = await AxiosInstance.get(`/api/services/city/${selectedCity}`);
            } else {
                safeSetState(setServices, []);
                return;
            }

            if (isMountedRef.current) {
                const servicesData = response.data.services || response.data || [];
                safeSetState(setServices, filterDuplicateServices(Array.isArray(servicesData) ? servicesData : []));
            }
        } catch (error) {
            console.error("Erreur lors du chargement des services:", error);
            if (isMountedRef.current) {
                safeSetState(setServices, []);
            }
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    };

    const filterDuplicateServices = (data) => {
        const seen = new Set();
        return data.filter(s => {
            if (seen.has(s._id)) return false;
            seen.add(s._id);
            return true;
        });
    };

    const formatArrondissementsList = (arrondissements) => {
        if (!Array.isArray(arrondissements) || arrondissements.length === 0) return "N/A";
        const hasFullCity = arrondissements.find(arr => arr.id === 'full_city');
        if (hasFullCity) return "Toute la ville";
        return arrondissements.map(arr => arr.name).join(', ');
    };

    const handleEditService = (service) => {
        startEditService(service);
    };

    const deleteService = (serviceId) => {
        showConfirmDialog(
            "Voulez-vous vraiment supprimer ce service ?",
            async () => {
                try {
                    await AxiosInstance.delete(`/api/services/${serviceId}`);
                    if (isMountedRef.current) {
                        showModal("Service supprimé avec succès !");
                        loadServices();
                        if (selectedCity) {
                            const res = await AxiosInstance.get(`/api/services/stats/city/${selectedCity}`);
                            safeSetState(setStats, res.data);
                        }
                    }
                } catch (err) {
                    console.error(err);
                    showModal("Erreur lors de la suppression du service.");
                }
            }
        );
    };

    const publishService = async (serviceId, currentPublishStatus) => {
        const newStatus = !currentPublishStatus;
        const action = newStatus ? "publier" : "dépublier";
        const actionPast = newStatus ? "publié" : "dépublié";

        showConfirmDialog(
            `Voulez-vous vraiment ${action} ce service ?${newStatus ? ' Une fois publié, il sera visible par les utilisateurs.' : ' Une fois dépublié, il ne sera plus visible par les utilisateurs.'}`,
            async () => {
                try {
                    const updateData = {
                        isPublish: newStatus
                    };

                    if (newStatus && packId) {
                        updateData.id_pack = packId;
                    }

                    await AxiosInstance.put(`/api/services/${serviceId}`, updateData);

                    showModal(`Service ${actionPast} avec succès !`);
                    loadServices();

                    if (selectedCity) {
                        const res = await AxiosInstance.get(`/api/services/stats/city/${selectedCity}`);
                        if (isMountedRef.current) {
                            safeSetState(setStats, res.data);
                        }
                    }
                } catch (error) {
                    console.error(`Erreur lors de la ${action}ation:`, error);
                    showModal(`Erreur lors de la ${action}ation du service.`);
                }
            }
        );
    };

    const handleResetFilters = () => {
        setSelectedCity("");
        setSelectedArrondissement("");
        setSearchQuery("");
    };

    return (
        <div className="tab-content active">
            <h2><strong className="label-black">Liste des Services</strong></h2>

            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-number">{stats?.totalServices ?? "-"}</div>
                    <div className="stat-label">Total Services</div>
                </div>
                <div className="stat-card">
                    <div className="stat-number">{stats?.publishedServices ?? "-"}</div>
                    <div className="stat-label">Services Publiés</div>
                </div>
                <div className="stat-card">
                    <div className="stat-number">{stats?.draftServices ?? "-"}</div>
                    <div className="stat-label">Brouillons</div>
                </div>
                <div className="stat-card">
                    <div className="stat-number">{stats?.servicesWithBadge ?? "-"}</div>
                    <div className="stat-label">Services Badge</div>
                </div>
            </div>

            <div className="filter-section">
                <h3><strong className="label-black">Filtres</strong></h3>
                <div className="filter-grid">
                    <div className="form-group">
                        <label>Filtrer par Ville</label>
                        <select
                            className="form-control"
                            value={selectedCity}
                            onChange={(e) => setSelectedCity(e.target.value)}
                        >
                            <option value="">Toutes les villes</option>
                            {Array.isArray(cities) && cities.map(c =>
                                <option key={c._id} value={c._id}>{c.name}</option>
                            )}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Filtrer par Arrondissement</label>
                        <select
                            className="form-control"
                            value={selectedArrondissement}
                            onChange={(e) => setSelectedArrondissement(e.target.value)}
                        >
                            <option value="">Tous les arrondissements</option>
                            {Array.isArray(arrondissements) && arrondissements.map(a =>
                                <option key={a._id} value={a._id}>{a.name}</option>
                            )}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Recherche par titre</label>
                        <input
                            className="form-control"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Rechercher un service..."
                        />
                    </div>
                    <div className="form-group">
                        <button className="btn btn-secondary" onClick={handleResetFilters}>
                            Réinitialiser
                        </button>
                    </div>
                </div>
            </div>

            {loading ? (
                <div style={{ textAlign: "center" }}>
                    <div className="loading"></div>
                    <p>Chargement des services...</p>
                </div>
            ) : (
                <div className="service-list">
                    {paginatedServices.length === 0 ? (
                        <p style={{ textAlign: "center", color: "#666", fontSize: "1.2rem", gridColumn: "1 / -1" }}>
                            Aucun service trouvé.
                        </p>
                    ) : (
                        paginatedServices.map((s) => (
                            <div key={s._id} className="service-card">
                                <div className="service-card-header">
                                    <div className="service-title">
                                        {s.title}
                                        {s.is_badge && <span className="badge">BADGE</span>}
                                    </div>
                                    <div className="service-actions">
                                        <button
                                            className="btn btn-sm btn-primary"
                                            style={{ padding: "0.25rem 0.5rem" }}
                                            onClick={() => handleEditService(s)}
                                            aria-label={`Modifier le service ${s.title}`}
                                        >
                                            ✏️ Modifier
                                        </button>

                                        <button
                                            className="btn btn-sm btn-info"
                                            style={{ padding: "0.25rem 0.5rem" }}
                                            onClick={() => {
                                                setCurrentServiceId(s._id);
                                                loadServiceEtapes(s._id);
                                                setTab("etapes");
                                            }}
                                            aria-label={`Voir les étapes du service ${s.title}`}
                                        >
                                            📋 Étapes
                                        </button>

                                        <button
                                            className="btn btn-sm btn-danger d-flex flex-column align-items-center justify-content-center"
                                            style={{ padding: "0.3rem 0.6rem", gap: "2px" }}
                                            onClick={() => deleteService(s._id)}
                                            aria-label={`Supprimer le service ${s.title}`}
                                        >
                                            <Trash2 size={16} />
                                            <span style={{ fontSize: "0.75rem" }}>Supprimer</span>
                                        </button>
                                    </div> 
                                </div>

                                <div className="service-description">{s.description}</div>

                                <div className="service-details">
                                    <div><strong className="label-black">Ville:</strong> {s.cityName || "N/A"}</div>

                                    <div>
                                        <strong className="label-black">Arrondissements:</strong>{" "}
                                        <span className="arrondissements-list">
                                            {formatArrondissementsList(s.arrondissements)}
                                        </span>
                                    </div>

                                    <div>
                                        <strong className="label-black">Catégorie:</strong>{" "}
                                        {
                                            typeof s.category === "object" && s.category?.title
                                                ? s.category.title
                                                : Array.isArray(categories) && categories.find(cat =>
                                                    cat._id === s.category || cat._id === s.id_TypeOfCategory
                                                )?.title
                                                || s.categoryName || "N/A"
                                        }
                                    </div>

                                    <div><strong className="label-black">Temps moyen:</strong> {s.average_time || "N/A"}</div>
                                    <div><strong className="label-black">Coût estimé:</strong> {s.estimated_cost || "N/A"}</div>

                                    {s.pricing && (
                                        <div className="pricing-info">
                                            <strong className="label-black">Tarification:</strong><br />
                                            <strong className="label-black">Durée: </strong> {s.pricing.estimated_duration ? `${s.pricing.estimated_duration} min` : 'N/A'}<br />
                                            <strong className="label-black">Prix complet:</strong> {formatXaf(s.pricing.full_price)} ({formatEurFromXaf(s.pricing.full_price)} indicatif)
                                        </div>
                                    )}

                                    <div className="etapes-summary">
                                        <strong className="label-black">Étapes:</strong>{" "}
                                        {s.etapesCount ? (
                                            <span className="etapes-count">{s.etapesCount} étape(s)</span>
                                        ) : (
                                            <span className="etapes-none">Aucune étape</span>
                                        )}
                                    </div>
                                </div>
                                <div className="service-meta">
                                    <span className={`service-status ${s.isPublish ? "status-published" : "status-draft"}`}>
                                        {s.isPublish ? "📢 Publié" : "📝 Brouillon"}
                                     </span>
                                     <small>Créé le {new Date(s.createdAt).toLocaleDateString()}</small>

                                     {/* Bouton de publication/dépublication dynamique */}
                                     <div className="publication-controls">
                                         {s.etapesCount > 0 ? (
                                            <button
                                                className={`btn btn-sm ${s.isPublish ? 'btn-warning' : 'btn-success'}`}
                                                onClick={() => publishService(s._id, s.isPublish)}
                                                style={{ marginLeft: "10px" }}
                                                title={s.isPublish ?
                                                    "Cliquez pour dépublier ce service" :
                                                    "Cliquez pour publier ce service"
                                                }
                                            >
                                                {s.isPublish ? "🔒 Dépublier" : "📢 Publier"}
                                            </button>
                                        ) : (
                                            <button
                                                className="btn btn-sm btn-disabled"
                                                disabled
                                                title="Impossible de publier : aucune étape définie"
                                                style={{ marginLeft: "10px" }}
                                            >
                                                ❌ Aucune étape
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )} 
            {totalPages > 1 && (
                <div className="pagination">
                {/* Premier */}
                <button
                className="pagination-btn"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                >
                «
                </button>
            
                {/* Précédent */}
                <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                >
                &lt; Précédent
                </button>
            
                {/* Pages : 1, 2, 3 */}
                {[...Array(Math.min(3, totalPages)).keys()].map((i) => (
                <button
                    key={i}
                    className={`pagination-btn ${currentPage === i + 1 ? "active" : ""}`}
                    onClick={() => handlePageChange(i + 1)}
                >
                    {i + 1}
                </button>
                ))}
            
                {/* Ellipsis si nécessaire */}
                {totalPages > 6 && <span className="pagination-ellipsis">...</span>}
            
                {/* Dernières pages : totalPages - 2, totalPages - 1, totalPages */}
                {totalPages > 3 && (
                [...Array(3).keys()].map((i) => {
                    const page = totalPages - 2 + i;
                    // On évite les doublons si les pages se chevauchent
                    if (page <= 3) return null;
                    return (
                    <button
                        key={page}
                        className={`pagination-btn ${currentPage === page ? "active" : ""}`}
                        onClick={() => handlePageChange(page)}
                    >
                        {page}
                    </button>
                    );
                })
                )}
            
                {/* Suivant */}
                <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                >
                Suivant &gt;
                </button>
            
                {/* Dernier */}
                <button
                className="pagination-btn"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                >
                »
                </button>
            </div>
            
                )}
        </div>
    );
};

export default ServiceList;



